import React from 'react';
import {
  PageSection,
  SectionType,
  SiteSettings,
  IndustryType,
  YachtItem,
  BlogPost,
  SiteData,
  SitePage,
} from '../../types';
import { SECTION_RENDERERS } from './registry';
import { EyeOff } from 'lucide-react';
import { getSectionThemeVariables } from '../../utils/contrast';
import { AnimationWrapper } from '../animation/AnimationWrapper';

interface SectionRendererProps {
  section: PageSection;
  settings?: SiteSettings;
  industry?: IndustryType;
  siteData?: SiteData;
  activePage?: SitePage;
  isEditor?: boolean;
  isSelected?: boolean;
  forcePlayKey?: number | string;
  deviceMode?: 'desktop' | 'tablet' | 'mobile';
  onSelect?: () => void;
  onNavigate?: (slug: string) => void;
  onSelectYacht?: (yacht: YachtItem) => void;
  onOpenReservationModal?: (yacht?: YachtItem) => void;
  onOpenBlogPost?: (post: BlogPost) => void;
  onOpenHeaderSettings?: () => void;
  onOpenImagePicker?: () => void;
  onAddService?: () => void;
}

export const SectionRenderer: React.FC<SectionRendererProps> = ({
  section,
  settings,
  industry,
  siteData,
  activePage,
  isEditor = false,
  isSelected = false,
  forcePlayKey,
  deviceMode,
  onSelect,
  onNavigate,
  onSelectYacht,
  onOpenReservationModal,
  onOpenBlogPost,
  onOpenHeaderSettings,
  onOpenImagePicker,
  onAddService,
}) => {
  // Construct fallback siteData if not provided directly
  const effectiveSiteData: SiteData = siteData || {
    id: 'preview-site',
    ownerId: 'preview-user',
    name: settings?.siteName || 'Adiosoft',
    slug: 'site',
    subdomain: 'site',
    industry: industry || 'CORPORATE',
    templateId: 'corp-apex',
    status: 'PUBLISHED',
    createdAt: Date.now(),
    updatedAt: Date.now(),
    settings: settings || {
      siteName: 'Adiosoft',
      phone: '+90 212 800 50 00',
      email: 'info@siteniz.com',
      address: 'İstanbul / Türkiye',
      primaryColor: '#0F172A',
      secondaryColor: '#334155',
      accentColor: '#2563EB',
      fontFamily: 'Inter, sans-serif',
      headingFont: 'Plus Jakarta Sans, sans-serif',
    },
    pages: [],
  };

  const effectiveActivePage: SitePage = activePage || {
    id: 'page-active',
    siteId: effectiveSiteData.id,
    title: 'Ana Sayfa',
    slug: '/',
    isHomePage: true,
    order: 0,
    sections: [section],
  };

  if (!section.isVisible && !isEditor) {
    return null;
  }

  const normalizedType = ((section.type || '') as string).toUpperCase() as SectionType;
  const RendererComponent = SECTION_RENDERERS[normalizedType] || SECTION_RENDERERS[section.type];

  // Resolve semantic tokens for this section
  const sectionTheme = getSectionThemeVariables(section.style, effectiveSiteData.settings);

  return (
    <div
      onClick={isEditor ? onSelect : undefined}
      data-section-wrapper-id={section.id}
      style={sectionTheme.cssVariables as React.CSSProperties}
      className={`relative transition-all website-section ${
        isEditor ? 'cursor-pointer' : ''
      } ${
        isEditor && isSelected
          ? 'ring-2 ring-blue-500 ring-offset-2 z-10'
          : isEditor
          ? 'hover:ring-1 hover:ring-blue-400/50'
          : ''
      } ${!section.isVisible ? 'opacity-40 grayscale' : ''}`}
    >
      {/* Hidden Section Badge in Editor */}
      {!section.isVisible && isEditor && (
        <div className="absolute top-2 left-4 z-20 bg-slate-900/90 text-amber-400 text-[10px] font-bold px-2.5 py-1 rounded-md border border-amber-500/30 flex items-center gap-1 shadow-md">
          <EyeOff className="w-3 h-3" />
          <span>Gizli Bölüm (Ziyaretçilere Kapalı)</span>
        </div>
      )}

      {RendererComponent ? (
        <AnimationWrapper
          config={section.animation || section.style?.animation}
          isEditor={isEditor}
          forcePlayKey={forcePlayKey}
          deviceMode={deviceMode}
        >
          <RendererComponent
            section={section}
            siteData={effectiveSiteData}
            activePage={effectiveActivePage}
            isPreview={!isEditor}
            onNavigate={onNavigate}
            onSelectYacht={onSelectYacht || onOpenReservationModal}
            onSelectPost={onOpenBlogPost}
            onOpenHeaderSettings={onOpenHeaderSettings}
            onOpenImagePicker={onOpenImagePicker}
            onAddService={onAddService}
          />
        </AnimationWrapper>
      ) : (
        <div className="p-8 text-center bg-slate-100 text-slate-500 border border-dashed border-slate-300">
          <p className="text-xs font-mono">Bilinmeyen Bölüm Tipi: {section.type}</p>
        </div>
      )}
    </div>
  );
};
